import React, { useEffect, useMemo, useState } from "react";
import Filter from "./Filter";
import InstanceTypeFilter from "./InstanceTypeFilter";
import PriceComparison from "./PriceComparision";
import Button from "../shared/Button";
import { useFilterStore } from "../../store/filterStore";
import RangeSelector from "../RangeSelector";
import { useInstanceStore } from "../../store/useInstanceStore";
import type { Instance } from "../table/types";
import SmartFiltersAccordion from "./SmartFiltersAccordion";

const FilterBar: React.FC = () => {
  const [isFieldsModalOpen, setIsFieldsModalOpen] = useState(false);
  const fieldsModalButtonRef = React.useRef<HTMLButtonElement>(null);
  const openFieldsModal = () => setIsFieldsModalOpen(true);
  const closeFieldsModal = () => setIsFieldsModalOpen(false);

  const resetFilters = useFilterStore((state) => state.resetFilters);

  const vcpuRange = useFilterStore((s) => s.vcpuRange);
  const memoryRange = useFilterStore((s) => s.memoryRange);
  const priceRange = useFilterStore((s) => s.priceRange);
  const cpuArchitecture = useFilterStore((s) => s.cpuArchitecture);

  const setVcpuRange = useFilterStore((s) => s.setVcpuRange);
  const setMemoryRange = useFilterStore((s) => s.setMemoryRange);
  const setPriceRange = useFilterStore((s) => s.setPriceRange);

  const data = useInstanceStore((state) => state.data);
  const providerType = useInstanceStore((state) => state.providerType);
  const setFilterData = useInstanceStore((state) => state.setFilterData);
  const columnVisibility = useFilterStore((state) => state.columnVisibility);
  const resetColumnVisibility = useFilterStore((state) => state.resetColumnVisibility);

  const hiddenCount = Object.values(columnVisibility).filter((v) => !v).length;

  function getMinMax(data: Instance[], key: keyof Instance): [number, number] {
    const nums = data.map((item) => Number(item[key])).filter((n) => !isNaN(n));
    if (nums.length === 0) return [0, 0];
    return [Math.min(...nums), Math.max(...nums)];
  }

  // Get min/max price based on providerType
  const getPriceMinMax = (data: Instance[], provider: "aws" | "gcp" | "azure"): [number, number] => {
    const prices = data
      .map((item) => {
        const price = item[provider];
        if (price === "-" || price === undefined || price === null) return null;
        return typeof price === "number" ? price : parseFloat(String(price));
      })
      .filter((p): p is number => p !== null && !isNaN(p));

    if (prices.length === 0) return [0, 0];
    return [Math.min(...prices), Math.max(...prices)];
  };

  // Compute min/max from data
  const [minVcpu, maxVcpu] = getMinMax(data, "vcpus");
  const [minMemory, maxMemory] = getMinMax(data, "memory");

  // Compute min/max price based on providerType
  const [minPrice, maxPrice] = useMemo(() => getPriceMinMax(data, providerType), [data, providerType]);

  // Set default ranges when data loads or providerType changes
  useEffect(() => {
    if (!data.length) return;

    const [minVcpu, maxVcpu] = getMinMax(data, "vcpus");
    const [minMemory, maxMemory] = getMinMax(data, "memory");
    const [minPrice, maxPrice] = getPriceMinMax(data, providerType);

    setVcpuRange([minVcpu, maxVcpu]);
    setMemoryRange([minMemory, maxMemory]);
    setPriceRange([minPrice, maxPrice]);

    setFilterData(data);
  }, [data, providerType, setVcpuRange, setMemoryRange, setPriceRange, setFilterData]);

  // 🔥 Filter instances dynamically when range sliders, price range, and cpuArchitecture change
  useEffect(() => {
    if (!data.length) return;

    const filtered = data.filter((item) => {
      const cpu = Number(item.vcpus);
      const mem = Number(item.memory);

      // Get price for the selected provider
      const price = item[providerType];
      const priceNum =
        price === "-" || price === undefined || price === null
          ? null
          : typeof price === "number"
            ? price
            : parseFloat(String(price));

      const matchesRange =
        cpu >= vcpuRange[0] &&
        cpu <= vcpuRange[1] &&
        mem >= memoryRange[0] &&
        mem <= memoryRange[1];

      const matchesPrice =
        priceNum === null ||
        (priceNum >= priceRange[0] && priceNum <= priceRange[1]);

      const matchesArchitecture =
        !cpuArchitecture || item.cpuArchitecture === cpuArchitecture;

      return matchesRange && matchesPrice && matchesArchitecture;
    });

    setFilterData(filtered);
  }, [vcpuRange, memoryRange, priceRange, cpuArchitecture, providerType, data, setFilterData]);

  return (
    <div className="w-full bg-white p-4 rounded-2xl shadow-sm flex flex-col gap-4">
      <PriceComparison className="w-full" />
      <Filter />
      <div className="flex flex-wrap gap-4 w-full">
        <InstanceTypeFilter />
      </div>

      <div className="flex gap-6 flex-wrap w-full mt-4">
        {/* vCPU Range */}
        <div className="flex-1 min-w-[280px]">
          <RangeSelector
            label="vCPUs"
            min={minVcpu}
            max={maxVcpu}
            value={vcpuRange}
            onChange={setVcpuRange}
          />
        </div>

        {/* Memory Range */}
        <div className="flex-1 min-w-[280px]">
          <RangeSelector
            label="Memory (GB)"
            min={minMemory}
            max={maxMemory}
            value={memoryRange}
            onChange={setMemoryRange}
          />
        </div>

        {/* Price Range */}
        <div className="flex-1 min-w-[280px]">
          <RangeSelector
            label={`Price (${providerType.toUpperCase()})`}
            min={minPrice}
            max={maxPrice}
            value={priceRange}
            onChange={setPriceRange}
          />
        </div>
      </div>
      <div className="flex gap-4 justify-end relative">
        <Button
          ref={fieldsModalButtonRef}
          variant="ghost"
          size="md"
          onClick={openFieldsModal}
          className="self-end flex items-center gap-2"
        >
          Manage Fields

          {hiddenCount > 0 && (
            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
              {hiddenCount} hidden
            </span>
          )}

          {hiddenCount > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                resetColumnVisibility();
              }}
              className="ml-1 cursor-pointer text-sm"
            >
              ×
            </span>
          )}
        </Button>

        <Button
          variant="ghost"
          size="md"
          onClick={resetFilters}
          className="self-end"
        >
          Reset Filters
        </Button>
        <SmartFiltersAccordion
          isOpen={isFieldsModalOpen}
          onClose={closeFieldsModal}
          buttonRef={fieldsModalButtonRef}
          showCPUArchitectureFilter={false}
        />
      </div>
    </div>
  );
};

export default FilterBar;
